import {
  FieldOption,
  OperatorOption,
  LogicalOperator,
} from '@/components/publicSearch/constants';
import type {
  FilterGroup,
  FilterInput,
} from '@/components/publicSearch/constants';
import type {
  ComparisonOperator,
  FilterGroupOfMemberFilteredFilterOptionInput,
  LogicalOperator as GqlLogicalOperator,
  MemberFilterField,
  MemberFilteredFilterOptionInput,
} from '@/graphqlClient/types';
import { parseRangeCompositeValue } from '@/components/publicSearch/search/rangeValue';
import {
  MEMBER_LOOKUP_FIELD_PREFIX,
  getMemberLookupResolverFromSearchSettings,
} from '@/components/publicSearch/search/constants';
import { expandI6LookupMarkerFilters } from '@/components/publicSearch/search/expandI6LookupFilters';
import { isIdentChipStyleFilter } from '@/components/publicSearch/search/animalIdentChipFilter';
import {
  processIdentChipFilterToGql,
  type GqlFilterSubGroup,
} from '@/components/publicSearch/search/identChipGql';

type SettingsArray =
  | Array<{ societySettingType?: string | null; value?: string | null }>
  | null
  | undefined;

const MEMBER_SEARCH_FIELDS = ['MEMB_NAME', 'MEMBER_IDENT', 'PREFIX'] as const;

function expandLookupFilters(
  filters: FilterInput[],
  settings: SettingsArray,
): ReturnType<typeof expandI6LookupMarkerFilters> {
  const resolver = getMemberLookupResolverFromSearchSettings(settings);
  return expandI6LookupMarkerFilters(
    filters,
    resolver,
    MEMBER_LOOKUP_FIELD_PREFIX,
  );
}

function expandBetweenFilters(filters: FilterInput[]): FilterInput[] {
  return filters.flatMap((filter) => {
    if (filter.operator === OperatorOption.between.value) {
      const parsedRange = parseRangeCompositeValue(filter.value);
      const expanded: FilterInput[] = [];
      if (parsedRange?.min?.trim()) {
        expanded.push({
          field: filter.field,
          operator: OperatorOption.greaterThanOrEqual.value,
          value: parsedRange.min.trim(),
        });
      }
      if (parsedRange?.max?.trim()) {
        expanded.push({
          field: filter.field,
          operator: OperatorOption.lessThanOrEqual.value,
          value: parsedRange.max.trim(),
        });
      }
      return expanded;
    }
    return [filter];
  });
}

const MEMBER_BRANCH_GQL_FIELDS = new Set([
  'MEMBER_IDENT',
  'MEMBER_NAME',
  'PREFIX',
]);

function mapMemberSearchFields(filters: FilterInput[]): FilterInput[] {
  return filters.map((filter) => {
    if (MEMBER_BRANCH_GQL_FIELDS.has(filter.field)) {
      return filter;
    }
    // Society custom overflow fields carry the internal UI token SOC_ME_FIELD{n}
    // (from MEMBER_SETTING_FIELD_TO_FIELD_OPTION / the member search-field parser).
    // The GraphQL MemberFilterField enum spells them SOCIETY_MEMBER_FIELD{n}, so
    // translate here at build time — mirrors how the animal path maps
    // SocietyAnimalField{n} -> SOCIETY_ANIMAL_FIELD{n} in transformFieldName. Without
    // this, SOC_ME_FIELD{n} would be sent verbatim as an invalid enum value.
    const socFieldMatch = filter.field.match(/^SOC_ME_FIELD(\d+)$/);
    if (socFieldMatch) {
      return { ...filter, field: `SOCIETY_MEMBER_FIELD${socFieldMatch[1]}` };
    }
    const field = MEMBER_SEARCH_FIELDS.includes(
      filter.field as (typeof MEMBER_SEARCH_FIELDS)[number],
    )
      ? 'MEMBER'
      : filter.field;
    return { ...filter, field };
  });
}

function mapMemberIdentChipSubGroup(
  sg: GqlFilterSubGroup,
): FilterGroupOfMemberFilteredFilterOptionInput {
  return {
    filters: toGqlMemberFilters(
      dedupeFilters(mapMemberSearchFields(sg.filters as FilterInput[])),
    ),
    groups: sg.groups?.map(mapMemberIdentChipSubGroup),
    logicalOperator: sg.logicalOperator as GqlLogicalOperator,
  };
}

function splitMemberIdentChipFilters(filters: FilterInput[]): {
  chipProcessed: FilterInput[];
  chipSubGroups: FilterGroupOfMemberFilteredFilterOptionInput[];
  rest: FilterInput[];
} {
  const chipProcessed: FilterInput[] = [];
  const chipSubGroups: FilterGroupOfMemberFilteredFilterOptionInput[] = [];
  const rest: FilterInput[] = [];

  for (const filter of filters) {
    const isMemberChip =
      (filter.field === FieldOption.member.value ||
        filter.field === 'MEMBER') &&
      typeof filter.value === 'string' &&
      isIdentChipStyleFilter(filter.operator, filter.value);

    if (!isMemberChip) {
      rest.push(filter);
      continue;
    }

    const { processed, subGroups } = processIdentChipFilterToGql(
      'MEMBER',
      filter.value,
      (f) => f,
    );
    chipProcessed.push(...(processed as FilterInput[]));
    chipSubGroups.push(...subGroups.map(mapMemberIdentChipSubGroup));
  }

  return { chipProcessed, chipSubGroups, rest };
}

function dedupeFilters(filters: FilterInput[]): FilterInput[] {
  const seen = new Set<string>();
  return filters.filter((f) => {
    const key = `${f.field}\t${f.operator}\t${f.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isCompleteFilter(filter: FilterInput): boolean {
  if (!filter.field) return false;

  const valueStr =
    typeof filter.value === 'string'
      ? filter.value
      : String(filter.value ?? '');

  if (
    (filter.field === FieldOption.member.value || filter.field === 'MEMBER') &&
    isIdentChipStyleFilter(filter.operator, valueStr)
  ) {
    return valueStr
      .split(',')
      .map((s) => s.trim())
      .some(Boolean);
  }

  if (!filter.operator) return false;
  if (filter.operator === OperatorOption.isNotEmpty.value) return true;
  return !!valueStr.trim();
}

function toGqlMemberFilters(
  filters: FilterInput[],
): MemberFilteredFilterOptionInput[] {
  return filters.map((filter) => ({
    field: filter.field as MemberFilterField,
    operator: filter.operator as ComparisonOperator,
    value: filter.value,
  }));
}

function buildLookupOrSubGroups(
  orGroups: FilterInput[][],
): FilterGroupOfMemberFilteredFilterOptionInput[] {
  return orGroups.map((group) => ({
    filters: toGqlMemberFilters(dedupeFilters(mapMemberSearchFields(group))),
    logicalOperator: LogicalOperator.or,
  }));
}

export function buildMemberFilterGroups(
  filters: FilterInput[],
  commonFilterOperator: GqlLogicalOperator | string,
  settings?: SettingsArray,
): FilterGroupOfMemberFilteredFilterOptionInput[] {
  const { filters: lookupExpanded, orGroups } = expandLookupFilters(
    filters,
    settings,
  );
  const { chipProcessed, chipSubGroups, rest } =
    splitMemberIdentChipFilters(lookupExpanded);
  const expanded = expandBetweenFilters([...rest, ...chipProcessed]);
  const mapped = mapMemberSearchFields(expanded);
  const deduped = dedupeFilters(mapped);
  const lookupOrSubGroups = buildLookupOrSubGroups(orGroups);
  const groups = [...chipSubGroups, ...lookupOrSubGroups];

  return [
    {
      logicalOperator: commonFilterOperator as GqlLogicalOperator,
      filters: toGqlMemberFilters(deduped),
      groups: groups.length > 0 ? groups : undefined,
    },
  ] as FilterGroupOfMemberFilteredFilterOptionInput[];
}

/**
 * Builds the GraphQL filterGroups payload from the AdvancedFilterDialog's
 * grouped state (multiple groups, each with its own logical operator,
 * combined by `groupsOperator`). Mirrors `buildAnimalFilterGroupsFromGroups`.
 *
 * Incomplete pills (empty field/operator/value) are dropped so the freshly
 * seeded empty group from the dialog doesn't reach the backend.
 */
export function buildMemberFilterGroupsFromGroups(
  filterGroups: FilterGroup[],
  groupsOperator: GqlLogicalOperator | string,
  settings?: SettingsArray,
): FilterGroupOfMemberFilteredFilterOptionInput[] {
  const gqlGroups = filterGroups
    .map((group) => {
      const completeFilters = group.filters.filter(isCompleteFilter);
      const { filters: lookupExpanded, orGroups } = expandLookupFilters(
        completeFilters,
        settings,
      );
      const { chipProcessed, chipSubGroups, rest } =
        splitMemberIdentChipFilters(lookupExpanded);
      const expanded = expandBetweenFilters([...rest, ...chipProcessed]);
      const mapped = mapMemberSearchFields(expanded);
      const deduped = dedupeFilters(mapped);
      const lookupOrSubGroups = buildLookupOrSubGroups(orGroups);
      const nestedGroups = [...chipSubGroups, ...lookupOrSubGroups];

      if (nestedGroups.length === 0) {
        return {
          filters: toGqlMemberFilters(deduped),
          logicalOperator: group.logicalOperator as GqlLogicalOperator,
        };
      }

      return {
        filters: toGqlMemberFilters(deduped),
        groups: nestedGroups,
        logicalOperator: group.logicalOperator as GqlLogicalOperator,
      };
    })
    .filter(
      (g) => g.filters.length > 0 || (g.groups != null && g.groups.length > 0),
    );

  return [
    {
      filters: [],
      groups: gqlGroups,
      logicalOperator: groupsOperator as GqlLogicalOperator,
    },
  ] as FilterGroupOfMemberFilteredFilterOptionInput[];
}
